'use client'

import React, { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Heart, Upload, Send, DollarSign } from 'lucide-react'
import { useTranslation } from 'react-i18next'

interface WellnessExpenseFormProps {
  staffId: string
  clinicId: string
  budgetRemaining?: number
  onSuccess?: () => void
}

export default function WellnessExpenseForm({
  staffId,
  clinicId,
  budgetRemaining = 0,
  onSuccess
}: WellnessExpenseFormProps) {
  const { t } = useTranslation('hr')
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState({
    expenseCategory: '',
    activityType: '',
    amount: 0,
    expenseDate: new Date().toISOString().split('T')[0],
    description: '',
    receiptUrl: ''
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Check budget
    if (formData.amount > budgetRemaining) {
      if (!confirm(t('wellness.exceedsBudgetConfirm', { budget: budgetRemaining }))) {
        return
      }
    }

    setLoading(true)

    try {
      const response = await fetch('/api/hr/wellness', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          staffId,
          clinicId,
          expenseCategory: formData.expenseCategory,
          activityType: formData.activityType,
          amount: formData.amount,
          expenseDate: formData.expenseDate,
          description: formData.description,
          receiptUrl: formData.receiptUrl
        })
      })

      const data = await response.json()

      if (data.success) {
        alert(t('wellness.submitSuccess'))
        onSuccess?.()
        // Reset form
        setFormData({
          expenseCategory: '',
          activityType: '',
          amount: 0,
          expenseDate: new Date().toISOString().split('T')[0],
          description: '',
          receiptUrl: ''
        })
      } else {
        alert(t('wellness.submitError') + ': ' + (data.error || 'Unknown error'))
      }
    } catch (error) {
      console.error('Submit error:', error)
      alert(t('wellness.submitErrorRetry'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Heart className="h-6 w-6" />
          {t('wellness.submitTitle')}
        </CardTitle>
        <CardDescription>
          {t('wellness.submitDescription')}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* Budget Warning */}
        {budgetRemaining > 0 && (
          <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">{t('wellness.availableBudget')}</span>
              <span className="text-lg font-bold text-blue-600">
                {budgetRemaining.toFixed(0)} SEK
              </span>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Expense Category */}
          <div className="space-y-2">
            <Label htmlFor="category">{t('wellness.category')}</Label>
            <Select
              value={formData.expenseCategory}
              onValueChange={(value) => setFormData({ ...formData, expenseCategory: value })}
              required
            >
              <SelectTrigger>
                <SelectValue placeholder={t('wellness.selectCategory')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="gym">{t('wellness.gym')}</SelectItem>
                <SelectItem value="wellness_activity">{t('wellness.wellnessActivity')}</SelectItem>
                <SelectItem value="equipment">{t('wellness.equipment')}</SelectItem>
                <SelectItem value="massage">{t('wellness.massage')}</SelectItem>
                <SelectItem value="physical_therapy">{t('wellness.physicalTherapy')}</SelectItem>
                <SelectItem value="mental_health">{t('wellness.mentalHealth')}</SelectItem>
                <SelectItem value="other">{t('wellness.other')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Activity Type */}
          <div className="space-y-2">
            <Label htmlFor="activityType">{t('wellness.activityType')}</Label>
            <Input
              id="activityType"
              type="text"
              placeholder={t('wellness.activityPlaceholder')}
              value={formData.activityType}
              onChange={(e) => setFormData({ ...formData, activityType: e.target.value })}
            />
          </div>

          {/* Amount */}
          <div className="space-y-2">
            <Label htmlFor="amount">{t('wellness.amount')}</Label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                id="amount"
                type="number"
                min="0"
                step="0.01"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) || 0 })}
                className="pl-9"
                required
              />
            </div>
            {formData.amount > budgetRemaining && budgetRemaining > 0 && (
              <p className="text-sm text-red-600">
                ⚠️ {t('wellness.exceedsBudget', { amount: (formData.amount - budgetRemaining).toFixed(0) })}
              </p>
            )}
          </div>

          {/* Date */}
          <div className="space-y-2">
            <Label htmlFor="expenseDate">{t('wellness.expenseDate')}</Label>
            <Input
              id="expenseDate"
              type="date"
              value={formData.expenseDate}
              onChange={(e) => setFormData({ ...formData, expenseDate: e.target.value })}
              required
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">{t('wellness.description')}</Label>
            <Textarea
              id="description"
              placeholder={t('wellness.descriptionPlaceholder')}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
            />
          </div>

          {/* Receipt Upload */}
          <div className="space-y-2">
            <Label htmlFor="receipt">{t('wellness.receipt')}</Label>
            <div className="flex gap-2">
              <Input
                id="receipt"
                type="url"
                placeholder={t('wellness.receiptPlaceholder')}
                value={formData.receiptUrl}
                onChange={(e) => setFormData({ ...formData, receiptUrl: e.target.value })}
                required
              />
              <Button type="button" variant="outline" size="icon">
                <Upload className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-sm text-muted-foreground">
              {t('wellness.receiptRequired')}
            </p>
          </div>

          {/* Submit Button */}
          <Button type="submit" disabled={loading} className="w-full">
            <Send className="h-4 w-4 mr-2" />
            {loading ? t('wellness.submitting') : t('wellness.submitForApproval')}
          </Button>

          <p className="text-xs text-muted-foreground text-center">
            {t('wellness.reviewNote')}
          </p>
        </form>
      </CardContent>
    </Card>
  )
}

